// The browser console only exists on the user's machine, so without this a production crash is invisible.

import { isChunkLoadError } from '../route-error-classify'
import { isHydrationError } from './hydration-mismatch'
// Static, because hydration-mismatch WANTS to evaluate early: it samples the <html lang> the document arrived with.
import { describeHydrationRewrite } from './hydration-rewrite-monitor'
import { counterSnapshot } from './counter-snapshot'
import { shouldReportFrontendError } from './frontend-error-filter'
import { versionDriftPending } from './app-version'
import { getBreadcrumbs } from './breadcrumbs'
import { noop } from '@tanstack/react-query'
import { queryClient, trpcClient } from '../main'

// Keeps a cyclic or absurdly deep chain from turning one report into a wall of text.
const MAX_CAUSE_DEPTH = 3

// Its OWN field, never folded into message or stack: both feed the server's fingerprint, so a per-occurrence
// cause in either would split one error into a new row — and a new GitHub issue — every time it happened.
function describeCauseChain(error: unknown): string | undefined {
  const parts: string[] = []
  try {
    let current: unknown = error
    for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
      if (!(current instanceof Error)) break
      const cause: unknown = current.cause
      if (cause == null) break
      if (cause instanceof Error) {
        parts.push(`Caused by: ${cause.name}: ${cause.message}\n${cause.stack ?? '(no stack)'}`)
      } else {
        parts.push(`Caused by: ${typeof cause === 'string' ? cause : safeStringify(cause)}`)
        break
      }
      current = cause
    }
  } catch {
    // A failure to enrich a report must never lose the report.
  }
  return parts.length === 0 ? undefined : parts.join('\n\n')
}

// A thrown value can be anything, so this never assumes instanceof Error.
export function extractErrorInfo(error: unknown): {
  name: string
  message: string
  stack: string | undefined
  cause?: string
} {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message || '(empty message)',
      stack: error.stack,
      cause: describeCauseChain(error),
    }
  }
  if (typeof error === 'string') {
    return { name: 'StringError', message: error, stack: undefined }
  }
  if (error == null) {
    return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined }
  }
  if (typeof error === 'object') {
    const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError'
    const message =
      'message' in error && typeof error.message === 'string'
        ? error.message
        : 'error' in error && typeof error.error === 'string'
          ? error.error
          : safeStringify(error)
    const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined
    return { name, message, stack }
  }
  return { name: 'UnknownError', message: safeStringify(error), stack: undefined }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

// Controlled server responses, already handled by toastErrorLink and logged where they were thrown — never the
// frontend exceptions this reporter exists for. Duck-typed, because bundling can change the class identity.
export function isTrpcClientError(error: unknown): boolean {
  if (typeof error !== 'object' || error == null) return false
  const e = error as { name?: unknown; data?: { code?: unknown } }
  if (e.name === 'TRPCClientError') return true
  return typeof e.data === 'object' && e.data != null && typeof e.data.code === 'string'
}

// React's wrapper for a Suspense boundary whose SERVER render threw, carrying the server's message after the colon.
const SERVER_RENDER_ERRORED = 'Switched to client rendering because the server rendering errored:'

/**
 * A server render that threw one of our machine codes (a bare UPPER_SNAKE token, the only message the tRPC boundary
 * sends): a controlled response, not a defect — a job this account can't see, a spent invitation. The same query
 * rethrows on the client as a TRPCClientError, which the route's error UI takes and this reporter skips.
 */
export function isServerRenderedTrpcError(message: string): boolean {
  if (!message.startsWith(SERVER_RENDER_ERRORED)) return false
  return /^[A-Z][A-Z0-9_]*$/.test(message.slice(SERVER_RENDER_ERRORED.length).trim())
}

/** Where an error was caught. `recoverable` is React's own error hooks — see `src/client.tsx`. */
export type FrontendErrorSource = 'boundary' | 'global' | 'unhandledrejection' | 'recoverable'

// namePrefix feeds the fingerprint, deliberately splitting one minified React error into separate issues by
// severity: a mismatch React patched over is not the same problem as one it could not recover from.
// componentStack rides in `stack`, never `message`, which would mint a fresh issue per component path.
export interface FrontendErrorExtra {
  namePrefix?: string
  componentStack?: string | null | undefined
}

// Fire-and-forget: never throws, never awaits the caller.
// Also the ONE place a hydration-class error is gated on who rewrote the document, so no capture site can drift.
export function logFrontendError(
  error: unknown,
  source: FrontendErrorSource = 'boundary',
  extra?: FrontendErrorExtra,
): void {
  // Browser only: server throws are logged where they happen, and trpcClient's links are window-coupled.
  if (typeof window === 'undefined') return
  // Controlled, expected responses rather than frontend exceptions — see isTrpcClientError.
  if (isTrpcClientError(error)) return
  // A stale chunk after a deploy is version skew, not a defect, and recordError would file an issue for every
  // user it caught mid-session. The boundary recovers it by waiting for reachability and reloading.
  if (isChunkLoadError(error)) return
  // The same skew one step further in: this document is running a build the server has already replaced, so the
  // frames are that build's and the fix may already be shipped (#88). The pending reload is the recovery.
  if (versionDriftPending()) return
  const info = extractErrorInfo(error)
  if (isServerRenderedTrpcError(info.message)) return
  // The visitor's environment talking, not our code: extensions, page translators, in-app WebViews, injected
  // bot harnesses. recordError files an issue on first production occurrence, so anything past here is a ticket.
  if (!shouldReportFrontendError(info.name, info.message, info.stack)) return
  // Only the leading blank line: a bare .trim() takes the first frame's indentation and misaligns the block.
  const componentStack = extra?.componentStack?.replace(/^\n+/, '').trimEnd()
  try {
    // A mismatch on a document something else rewrote is that rewriter's doing; asked only for hydration-class
    // errors, and it fails open, so an unrecognised rewrite still reports.
    const rewrite = isHydrationError(info.message) ? describeHydrationRewrite(queryClient) : undefined
    if (rewrite?.foreign) return
    trpcClient.common.logFrontendError
      .mutate({
        route: window.location.pathname,
        name: (extra?.namePrefix ?? '') + info.name,
        message: info.message,
        // Appended, not merged into `message`: see FrontendErrorExtra.
        stack: componentStack ? `${info.stack ?? '(no stack)'}\n\nComponent stack:\n${componentStack}` : info.stack,
        cause: info.cause,
        source,
        breadcrumbs: getBreadcrumbs(),
        // Why it got past the gate, and what the counters read as it did: otherwise "the page was pristine" and
        // "the check never ran" are the same silence. Attribution FIRST — the field truncates from the tail at
        // 600 chars, which a ten-counter snapshot can reach alone, dropping rewriteCheck on the richest reports.
        counters: rewrite ? [rewrite.attribution, counterSnapshot()].filter(Boolean).join(' || ') : undefined,
        // What actually differed: React reports `args[]=text` with an empty diff in production.
        mismatch: rewrite?.mismatch,
      })
      .catch(noop)
  } catch {
    // Swallow — reporting the error must never itself surface an error.
  }
}
